if (typeof OfferPack === 'undefined') {
  class OfferPack extends HTMLElement {
    static SELECTORS = {
      cards: '[data-offer-pack-card]',
      check: '.offer-pack__check',
      productForm: 'product-form form[action*="/cart/add"]',
      formVariantId: 'input[name="id"]',
      hiddenQty: 'input[data-offer-pack-quantity]',
      customizer: '[data-offer-pack-customizer]',
      customizerToggle: '[data-offer-pack-customizer-toggle]',
      customizerPanel: '[data-offer-pack-customizer-panel]',
      bundleCards: '[data-offer-pack-bundle-card]',
      bundleSizeSelect: '[data-offer-pack-bundle-size]',
      bundleSizeSelectDrawer: '[data-offer-pack-bundle-size-drawer]',
      bundleVariantsJson: 'script[data-offer-pack-bundle-variants]',
      bundleMetaJson: 'script[data-offer-pack-bundle-meta]',
      heroPrice: '[data-offer-pack-hero-price]',
      heroCompare: '[data-offer-pack-hero-compare]',
      drawerPrice: '[data-offer-pack-drawer-price]'
    };
    static CSS = {
      checkInactive: 'offer-pack__check--inactive',
      customizerOpen: 'offer-pack__customizer--open'
    };
    static EVENTS = {
      change: 'multi-add-to-cart:change'
    };
    static CARD_TYPES = {
      pack: 'pack',
      single: 'single'
    };
    static MODES = {
      pack: 'pack',
      bundle: 'bundle'
    };
    static BONUS_VARIANT_PREFIX = 'offer-pack-bonus-';

    /* ─── Lifecycle ──────────────────────────────────────────────────────── */

    connectedCallback () {
      this._abort = new AbortController();
      this._mode = this.dataset.offerPackMode || OfferPack.MODES.pack;
      this._cards = this.querySelectorAll(OfferPack.SELECTORS.cards);
      this._currentLines = [];
      // Bundle mode is "pack" by definition — there's no single alternative card.
      this._activeCardType = this._mode === OfferPack.MODES.bundle
        ? OfferPack.CARD_TYPES.pack
        : OfferPack.CARD_TYPES.single;

      this._readDataset();
      this._initBundleCustomizer();
      this._bindEvents();
      this._hookProductForm();

      if (this._mode === OfferPack.MODES.bundle) {
        // Bundle row is always live — wait for variant selection before emitting a line.
        this._currentLines = this._buildLines(this._activeCardType);
        this._dispatchChange();
      } else if (this._singleDiscountValue > 0) {
        // Pack mode with single_offer configured: the single card is active by
        // default, so emit its discount line right away for the summary.
        this._currentLines = this._buildLines(this._activeCardType);
        this._dispatchChange();
      }
    }

    disconnectedCallback () {
      this._abort?.abort();
    }

    /* ─── Public API ─────────────────────────────────────────────────────── */

    getLines () {
      return this._currentLines;
    }

    selectCard (card) {
      if (!card) return;
      // Bundle card: tapping anywhere on it (toggle hint included) opens /
      // closes the customizer panel and deactivates sibling offers.
      if (this._mode === OfferPack.MODES.bundle) {
        this._togglePanel();
        return;
      }

      this._applyCardSelection(card);
      // Any click on this pack offer makes IT the active offer in the block;
      // bump any sibling offer-pack(s) out of their active state.
      this._deactivateSiblings();
    }

    // Apply a card's selection state on this offer-pack: press the card,
    // sync the form quantity, recompute emitted lines. Sibling deactivation
    // is handled by the caller (`selectCard`) so this method stays reusable
    // without recursion risk.
    _applyCardSelection (card) {
      this._activeCardType = this._cardType(card);
      this._toggleSelectedCard(card);
      this._setVisibleQuantity(this._cardQty(card));
      this._setHiddenQuantity(this._cardQty(card));
      this._toggleCustomizer(this._activeCardType === OfferPack.CARD_TYPES.pack);
      this._currentLines = this._buildLines(this._activeCardType);
      this._dispatchChange();
    }

    /* ─── Init ───────────────────────────────────────────────────────────── */

    _readDataset () {
      this._baseVariantId = this.dataset.baseVariantId || '0';
      this._basePriceCents = this._toInt(this.dataset.basePriceCents, 0);
      // Final pack price + compare-at, precomputed by Liquid (the card's
      // `.offer-pack__price` / `.offer-pack__compare-price` values in cents).
      this._packPriceCents = this._toInt(this.dataset.price, this._basePriceCents);
      this._packCompareCents = this._toInt(this.dataset.compareAtPrice, this._basePriceCents);
      this._discountValue = this._toFloat(this.dataset.offerDiscountValue, 0);
      this._discountUnit = this.dataset.offerDiscountUnit || '';
      // Optional single-card discount — non-zero only when the merchant
      // configured `single_offer` on the offer_pack block.
      this._singleDiscountValue = this._toFloat(this.dataset.singleDiscountValue, 0);
      this._singleDiscountUnit = this.dataset.singleDiscountUnit || '';
    }

    _initBundleCustomizer () {
      this._customizer = this.querySelector(OfferPack.SELECTORS.customizer);
      if (!this._customizer) return;

      this._customizerPanel = this._customizer.querySelector(OfferPack.SELECTORS.customizerPanel);
      // The toggle hint can live either inside the bundle card (bundle mode) or
      // inside the customizer wrapper (pack mode). Query the whole component.
      this._customizerToggles = this.querySelectorAll(OfferPack.SELECTORS.customizerToggle);
      this._bundleCards = this._customizer.querySelectorAll(OfferPack.SELECTORS.bundleCards);
      this._bundleSizeSelect = this._customizer.querySelector(OfferPack.SELECTORS.bundleSizeSelect);
      this._heroPriceEl = this.querySelector(OfferPack.SELECTORS.heroPrice);
      this._heroCompareEl = this.querySelector(OfferPack.SELECTORS.heroCompare);

      // The size-guide drawer is rendered as a sibling of `<offer-pack>`
      // (drawers move in the DOM when shown), so we look it up by id from
      // the document scope rather than scoping to `this`.
      this._bundleSgDrawer = null;
      this._bundleSizeSelectDrawer = null;
      this._drawerPriceEl = null;
      const sgId = this.dataset.bundleSgId;
      if (sgId) {
        this._bundleSgDrawer = document.getElementById(sgId);
        this._bundleSizeSelectDrawer = this._bundleSgDrawer?.querySelector(OfferPack.SELECTORS.bundleSizeSelectDrawer) || null;
        this._drawerPriceEl = this._bundleSgDrawer?.querySelector(OfferPack.SELECTORS.drawerPrice) || null;
      }

      this._bundleVariants = this._readJson(OfferPack.SELECTORS.bundleVariantsJson) || [];
      const meta = this._readJson(OfferPack.SELECTORS.bundleMetaJson) || {};
      this._sizeOptionPosition = this._toInt(meta.sizeOptionPosition, 0);
      this._bundleProductMeta = meta; // companion product info (id/title/vendor/category/url/image) for tracking

      this._selectedCombo = '';
      this._selectedSize = '';
      this._selectedBundleVariant = null;

      // Liquid pre-marks a card with `aria-checked="true"` when the merchant
      // turned on the "preselect first combo" setting. Pick that up here so
      // the bundle resolves as soon as the shopper picks a size (or right
      // away when the linked product has no size option).
      const preChecked = Array.from(this._bundleCards || []).find(
        (card) => card.getAttribute('aria-checked') === 'true'
      );
      if (preChecked) {
        this._selectedCombo = preChecked.dataset.offerPackBundleCombo || '';
        this._resolveBundleVariant();
      }
    }

    _readJson (selector) {
      const tag = this.querySelector(selector);
      if (!tag?.textContent) return null;
      try {
        return JSON.parse(tag.textContent);
      } catch {
        return null;
      }
    }

    _bindEvents () {
      this._cards.forEach((card) => {
        card.addEventListener('click', (event) => this._onCardClick(event, card), { signal: this._abort.signal });
        // The bundle card is a `<div role="button">` (not a real `<button>`)
        // because the customizer panel nests inside it. Native keyboard
        // activation (Enter / Space) only works on real buttons, so we
        // emulate it for non-button cards here.
        if (card.tagName !== 'BUTTON') {
          card.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              this.selectCard(card);
            }
          }, { signal: this._abort.signal });
        }
      });

      if (!this._customizer) return;

      // Note: the bundle's `.offer-pack__customizer-toggle` is a visual hint
      // (a `<span>` inside the bundle card) — clicks bubble to the card's
      // own click handler, so we don't bind a separate listener for it.

      this._bundleCards?.forEach((card) => {
        card.addEventListener('click', () => this._selectBundleCard(card), { signal: this._abort.signal });
      });

      this._bundleSizeSelect?.addEventListener('change', (event) => {
        this._setSelectedSize(event.target.value || '', { source: 'panel' });
      }, { signal: this._abort.signal });

      // Two-way sync with the size selector inside the size-guide drawer.
      // The `source` flag prevents the cross-update from re-firing change
      // events and bouncing back.
      this._bundleSizeSelectDrawer?.addEventListener('change', (event) => {
        this._setSelectedSize(event.target.value || '', { source: 'drawer' });
      }, { signal: this._abort.signal });
    }

    // Single source of truth for the selected size — keeps both the panel's
    // and the drawer's `<select>` in sync, then triggers variant resolution.
    _setSelectedSize (value, { source } = {}) {
      this._selectedSize = value;
      if (source !== 'panel' && this._bundleSizeSelect && this._bundleSizeSelect.value !== value) {
        this._bundleSizeSelect.value = value;
      }
      if (source !== 'drawer' && this._bundleSizeSelectDrawer && this._bundleSizeSelectDrawer.value !== value) {
        this._bundleSizeSelectDrawer.value = value;
      }
      this._resolveBundleVariant();
    }

    // Card click handler — interactions that originate inside the customizer
    // panel (size select, bundle cards, size-guide link) must NOT trigger
    // `selectCard` (it would toggle the panel out from under the user).
    // We can't stop propagation either: the size-guide drawer relies on
    // document-level `[aria-controls]` delegation that needs the bubble.
    _onCardClick (event, card) {
      if (event.target.closest(OfferPack.SELECTORS.customizer)) return;
      this.selectCard(card);
    }

    /* ─── UI state ───────────────────────────────────────────────────────── */

    _toggleSelectedCard (selected) {
      this._cards.forEach((card) => {
        const isSelected = card === selected;
        card.setAttribute('aria-pressed', isSelected);
        card.querySelector(OfferPack.SELECTORS.check)
          ?.classList.toggle(OfferPack.CSS.checkInactive, !isSelected);
      });
    }

    _toggleCustomizer (show) {
      if (!this._customizer) return;
      if (this._mode === OfferPack.MODES.bundle) return; // bundle keeps the customizer permanently visible
      this._customizer.hidden = !show;
      if (!show) this._collapsePanel();
    }

    _togglePanel () {
      if (!this._customizerPanel) return;

      const firstToggle = this._customizerToggles?.[0];
      const expanded = firstToggle?.getAttribute('aria-expanded') === 'true';
      const opening = !expanded;
      this._setPanelOpen(opening);

      const bundleCard = this.querySelector(
        `[data-offer-pack-card="${OfferPack.CARD_TYPES.pack}"]`
      );

      if (opening) {
        // Bundle becomes the active offer in the block; deactivate siblings.
        bundleCard?.setAttribute('aria-pressed', 'true');
        this._deactivateSiblings();
      } else {
        // Bundle dismissed — the block needs another active card, so fall
        // back to the default (first card of the first <offer-pack>).
        bundleCard?.setAttribute('aria-pressed', 'false');
        this._restoreDefaultActive();
      }

      // Press state just flipped — recompute so the summary picks up (opening)
      // or drops (closing) this bundle's pack price.
      this._currentLines = this._buildLines(this._activeCardType);
      this._dispatchChange();
    }

    // Activate the "default" card across the block: the first card of the
    // first <offer-pack> in the same scope. Used when this offer is being
    // dismissed (bundle panel collapsed) — exactly one card must remain
    // pressed across the block at all times. No-op if this is the first
    // offer-pack itself (nothing else to fall back to).
    _restoreDefaultActive () {
      const scope = this.closest('product-info')
        || this.closest('[data-block-id]')?.parentElement
        || document;
      const firstOfferPack = scope.querySelector('offer-pack');
      if (!firstOfferPack || firstOfferPack === this) return;
      const firstCard = firstOfferPack.querySelector('[data-offer-pack-card]');
      if (firstCard) firstOfferPack.selectCard(firstCard);
    }

    // Reset every other <offer-pack> on the same product so only one offer is
    // actively selected at a time. Walks UP from the current offer-pack until
    // it finds a scope containing sibling offer-packs, then asks each peer to
    // visually deactivate itself.
    _deactivateSiblings () {
      const scope = this.closest('product-info')
        || this.closest('[data-block-id]')?.parentElement
        || document;
      scope.querySelectorAll('offer-pack').forEach((peer) => {
        if (peer === this) return;
        peer._deactivate?.();
      });
    }

    // Silent visual reset — no card on this offer-pack stays `aria-pressed`,
    // and the internals revert to a neutral baseline so the form quantity /
    // emitted lines reflect a dormant state. Used by `_deactivateSiblings`
    // when a sibling claims active focus. Never calls back into `selectCard`
    // (that would recursively undo the originating click on the sibling).
    _deactivate () {
      if (this._mode === OfferPack.MODES.bundle) {
        const card = this.querySelector(
          `[data-offer-pack-card="${OfferPack.CARD_TYPES.pack}"]`
        );
        card?.setAttribute('aria-pressed', 'false');
        this._collapsePanel();
        // No longer the active offer — drop this bundle's line from the summary.
        this._currentLines = this._buildLines(this._activeCardType);
        this._dispatchChange();
        return;
      }
      // Pack mode: un-press every card, mark every check inactive, fall back
      // to the "single" baseline (qty=1, no extra lines) without pressing it.
      this._cards.forEach((card) => {
        card.setAttribute('aria-pressed', 'false');
        card.querySelector(OfferPack.SELECTORS.check)
          ?.classList.add(OfferPack.CSS.checkInactive);
      });
      this._activeCardType = OfferPack.CARD_TYPES.single;
      const singleCard = this.querySelector('[data-offer-pack-card="single"]');
      if (singleCard) {
        this._setVisibleQuantity(this._cardQty(singleCard));
        this._setHiddenQuantity(this._cardQty(singleCard));
      }
      this._toggleCustomizer(false);
      this._currentLines = this._buildLines(this._activeCardType);
      this._dispatchChange();
    }

    _collapsePanel () {
      this._setPanelOpen(false);
    }

    _setPanelOpen (open) {
      if (!this._customizerPanel) return;
      this._customizerPanel.hidden = !open;
      this._customizerToggles?.forEach((toggle) => {
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        const icon = toggle.querySelector('.offer-pack__customizer-toggle-icon');
        if (icon) icon.textContent = open ? '−' : '+';
      });
      this._customizer?.classList.toggle(OfferPack.CSS.customizerOpen, open);
    }

    _selectBundleCard (card) {
      this._selectedCombo = card.dataset.offerPackBundleCombo || '';
      this._bundleCards.forEach((c) => {
        const isSelected = c === card;
        c.setAttribute('aria-checked', isSelected ? 'true' : 'false');
      });
      this._resolveBundleVariant();
    }

    /* ─── Bundle variant resolution ──────────────────────────────────────── */

    _resolveBundleVariant () {
      this._selectedBundleVariant = null;

      if (!this._selectedCombo) {
        this._refreshHeroPrice();
        this._currentLines = this._buildLines(this._activeCardType);
        this._dispatchChange();
        return;
      }

      const comboPairs = this._parseCombo(this._selectedCombo);
      const sizePos = this._sizeOptionPosition;
      const needSize = sizePos > 0;

      if (needSize && !this._selectedSize) {
        this._refreshHeroPrice();
        this._currentLines = this._buildLines(this._activeCardType);
        this._dispatchChange();
        return;
      }

      const match = this._bundleVariants.find((variant) => {
        for (const [pos, value] of comboPairs) {
          if (variant.options[pos - 1] !== value) return false;
        }
        if (needSize && variant.options[sizePos - 1] !== this._selectedSize) return false;
        return true;
      });

      this._selectedBundleVariant = match || null;
      this._refreshHeroPrice();
      this._currentLines = this._buildLines(this._activeCardType);
      this._dispatchChange();
    }

    _parseCombo (combo) {
      return combo.split('|')
        .filter(Boolean)
        .map((entry) => {
          const [posStr, ...valueParts] = entry.split(':');
          return [this._toInt(posStr, 0), valueParts.join(':')];
        });
    }

    // Recomputes the hero card's "discounted / compare" prices when the user
    // resolves to a specific linked variant. Falls back to the Liquid-rendered
    // values (no live update) when no variant is resolved yet. Also updates
    // the size-guide drawer's footer price when present.
    _refreshHeroPrice () {
      if (this._mode !== OfferPack.MODES.bundle) return;
      if (!this._selectedBundleVariant) return;

      const variantPriceCents = this._toInt(this._selectedBundleVariant.price, 0);
      const baseTotal = this._basePriceCents + variantPriceCents;
      const cheapest = Math.min(this._basePriceCents, variantPriceCents);
      const discounted = this._applyDiscount(baseTotal, cheapest);

      if (this._heroPriceEl) this._heroPriceEl.textContent = this._formatMoney(discounted);
      if (this._heroCompareEl) this._heroCompareEl.textContent = this._formatMoney(baseTotal);
      if (this._drawerPriceEl) this._drawerPriceEl.textContent = this._formatMoney(discounted);
    }

    // `discountBaseCents` is what the % is taken from — for bundles that's the
    // cheapest item ("-X% sur le 2e produit"), not the whole pack. Defaults to
    // the full total when not provided.
    _applyDiscount (totalCents, discountBaseCents = totalCents) {
      if (this._discountUnit === '%') {
        const discountAmount = Math.round(discountBaseCents * this._discountValue / 100);
        return Math.max(0, totalCents - discountAmount);
      }
      if (this._discountUnit === '€' || this._discountUnit === 'fixed') {
        const discountCents = Math.round(this._discountValue * 100);
        return Math.max(0, totalCents - discountCents);
      }
      return totalCents;
    }

    // Mirrors the `money_without_trailing_zeros` filter for the active currency
    // by reading the Liquid-rendered hero price's existing format.
    _formatMoney (cents) {
      const original = this._heroPriceEl?.textContent || '';
      const symbol = original.match(/[^\d\s,. ]/g)?.join('') || '€';
      const value = cents / 100;
      const hasDecimal = Math.round(cents) % 100 !== 0;
      const formatted = hasDecimal ? value.toFixed(2).replace('.', ',') : String(Math.round(value));
      // Preserve symbol position (prefix vs suffix) from the original render.
      const symbolFirst = original.trim().startsWith(symbol);
      return symbolFirst ? `${symbol}${formatted}` : `${formatted}${symbol}`;
    }

    /* ─── Form quantity sync ─────────────────────────────────────────────── */

    // The theme's own quantity field, resolved via form association: it may sit
    // outside <product-form> and bind through a `form=` attribute, so a DOM
    // descendant query misses it. Excludes the hidden input we inject ourselves.
    _themeQtyInput () {
      const form = document.querySelector(OfferPack.SELECTORS.productForm);
      if (!form) return null;
      return Array.from(form.elements).find(
        (el) => el.name === 'quantity' && el.dataset.offerPackQuantity === undefined
      ) || null;
    }

    _setVisibleQuantity (qty) {
      if (!qty) return;
      const input = this._themeQtyInput();
      if (!input) return;

      input.value = qty;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }

    // When the form already has its own `name="quantity"` input we just set it above;
    // injecting another would send two quantity values and /cart/add keeps the wrong one.
    // Only when the form has no quantity input at all do we add a hidden fallback.
    _setHiddenQuantity (qty) {
      if (!qty) return;
      const form = document.querySelector(OfferPack.SELECTORS.productForm);
      if (!form) return;

      let hidden = form.querySelector(OfferPack.SELECTORS.hiddenQty);
      if (this._themeQtyInput()) {
        hidden?.remove();
        return;
      }

      if (!hidden) {
        hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'quantity';
        hidden.dataset.offerPackQuantity = '';
        form.appendChild(hidden);
      }
      hidden.value = String(qty);
    }

    /* ─── Cart submission hook (bundle mode) ─────────────────────────────── */

    // Bundle mode: intercept the form submit (main CTA, sticky CTA, size-guide
    // footer — all submit the same form) and add the base + companion in ONE
    // atomic /cart/add.js instead of the native add + a delayed companion add.
    // Capture on document runs before the theme's bubble-phase submit handler;
    // stopImmediatePropagation also prevents sibling offer-pack instances from
    // double-adding (first one wins).
    _hookProductForm () {
      document.addEventListener('submit', (event) => this._onFormSubmit(event), {
        capture: true,
        signal: this._abort.signal
      });
    }

    _onFormSubmit (event) {
      const form = event.target;
      if (!form || !form.matches || !form.matches(OfferPack.SELECTORS.productForm)) return;

      // Only bundle mode contributes real companion lines; pack/single return none.
      const lines = this._cartAddableLines();
      if (lines.length === 0) return;

      const mainId = this._formVariantId(form);
      if (!mainId) return;

      // Committed to handling this submit — block the native add (and siblings).
      event.preventDefault();
      event.stopImmediatePropagation();

      if (!form.checkValidity()) { form.reportValidity(); return; }

      const items = [{ id: mainId, quantity: this._formQuantity() }].concat(
        lines.map((line) => ({ id: parseInt(line.variantId, 10), quantity: line.qty }))
      );
      if (typeof window.addToCart === 'function') {
        window.addToCart({ items }, true, false, true, false);
      }
    }

    _formVariantId (form) {
      const input = form.querySelector(OfferPack.SELECTORS.formVariantId);
      const id = input ? parseInt(input.value, 10) : NaN;
      return Number.isFinite(id) && id > 0 ? id : null;
    }

    _formQuantity () {
      const input = this._themeQtyInput();
      const qty = input ? parseInt(input.value, 10) : 1;
      return Number.isFinite(qty) && qty > 0 ? qty : 1;
    }

    // The bonus-line variant id (`offer-pack-bonus-…`) is virtual — only used
    // by the multi-add-summary for display. Filter it out here so we don't
    // POST a fake id to /cart/add.
    _cartAddableLines () {
      return this._currentLines.filter((line) => {
        if (!line?.variantId) return false;
        if (String(line.variantId).startsWith(OfferPack.BONUS_VARIANT_PREFIX)) return false;
        const numeric = parseInt(line.variantId, 10);
        return Number.isFinite(numeric) && numeric > 0;
      });
    }

    /* ─── Multi-add-summary integration ──────────────────────────────────── */

    _buildLines (cardType) {
      // Single card: emit a bonus line only when a single_offer is configured —
      // otherwise the parent's main /cart/add already carries the variant at
      // full price and the summary doesn't need an override.
      if (cardType === OfferPack.CARD_TYPES.single) {
        if (this._singleDiscountValue <= 0) return [];
        // `replacesBase: true` tells the summary to skip its default base-price
        // contribution — otherwise the single-with-discount line would *add* to
        // the base price (238€ instead of 109€ for a 129€ product with -20€).
        return [{
          variantId: `${OfferPack.BONUS_VARIANT_PREFIX}${this._baseVariantId}`,
          qty: 1,
          price: this._basePriceCents,
          discount: this._singleDiscountValue,
          discountUnit: this._singleDiscountUnit,
          replacesBase: true
        }];
      }

      if (cardType !== OfferPack.CARD_TYPES.pack) return [];

      // The card already renders the final pack price + compare-at (Liquid owns
      // the discount math). The summary mirrors those exact totals via a single
      // `replacesBase` line so it can never diverge from what the card shows.
      // `_packPriceCents` / `_packCompareCents` are the Liquid defaults; the
      // bundle path overrides them once a companion variant is resolved.
      let variantId = `${OfferPack.BONUS_VARIANT_PREFIX}${this._baseVariantId}`;
      let price = this._packPriceCents;
      let compareAtPrice = this._packCompareCents;
      let companion = null;

      if (this._mode === OfferPack.MODES.bundle) {
        // Bundle only feeds the summary while it's the active (pressed) offer —
        // otherwise the default offer's price must show instead.
        const bundleCard = this.querySelector(`[data-offer-pack-card="${OfferPack.CARD_TYPES.pack}"]`);
        if (bundleCard?.getAttribute('aria-pressed') !== 'true') return [];

        if (this._selectedBundleVariant) {
          // Real companion variant chosen — add it to the cart and price the
          // pack off (base + companion), matching `_refreshHeroPrice`. The % is
          // taken from the cheapest of the two items.
          const variantPrice = this._toInt(this._selectedBundleVariant.price, 0);
          variantId = String(this._selectedBundleVariant.id);
          compareAtPrice = this._basePriceCents + variantPrice;
          price = this._applyDiscount(compareAtPrice, Math.min(this._basePriceCents, variantPrice));
          companion = this._selectedBundleVariant;
        }
        // No variant yet: keep the Liquid default totals so the summary shows
        // the pack price immediately. The virtual id is filtered out at cart
        // submit, so nothing is added until the shopper resolves a variant.
      }

      const line = { variantId, qty: 1, price, compareAtPrice, replacesBase: true };
      // Carry the companion product's info so the multi-add-to-cart:change line
      // (real variant id, not the virtual base) has full item_* / item_group_* data.
      if (companion) {
        const m = this._bundleProductMeta || {};
        Object.assign(line, {
          productId: m.productId, productTitle: m.productTitle, vendor: m.vendor,
          category: m.category, url: m.url, image: m.image,
          sku: companion.sku, variantTitle: companion.title
        });
      }
      return [line];
    }

    _dispatchChange () {
      this.dispatchEvent(new CustomEvent(OfferPack.EVENTS.change, {
        bubbles: true,
        detail: { lines: this._currentLines }
      }));
    }

    /* ─── Helpers ────────────────────────────────────────────────────────── */

    _cardType (card) {
      return card.dataset.offerPackCard;
    }

    _cardQty (card) {
      return this._toInt(card.dataset.offerPackQuantity, 0);
    }

    _toInt (value, fallback) {
      const n = parseInt(value, 10);
      return Number.isFinite(n) ? n : fallback;
    }

    _toFloat (value, fallback) {
      const n = parseFloat(value);
      return Number.isFinite(n) ? n : fallback;
    }
  }

  if (!customElements.get('offer-pack')) {
    customElements.define('offer-pack', OfferPack);
  }
}
